"use client";

import React from "react";
import { EntityDisplay } from "fansunited-sports-ui";
import { cn } from "@/utilities/core/cn";

interface TeamComparisonHeaderProps {
    homeTeamName: string;
    awayTeamName: string;
    homeTeamLogo?: string;
    awayTeamLogo?: string;
    /** Custom image node for home team (e.g., <TeamLogo>) — takes precedence over homeTeamLogo */
    homeTeamImage?: React.ReactNode;
    /** Custom image node for away team (e.g., <TeamLogo>) — takes precedence over awayTeamLogo */
    awayTeamImage?: React.ReactNode;
    /** Optional subtitle for home team (e.g., "Home", formation) */
    homeSubtitle?: string;
    /** Optional subtitle for away team (e.g., "Away", formation) */
    awaySubtitle?: string;
    /** Layout variant: "simple" for 2-col, "with-center" for 5-col with center spacer */
    variant?: "simple" | "with-center";
    /** Text overflow handling: "truncate" or "break-words" */
    textOverflow?: "truncate" | "break-words";
    /** Additional class name for the container */
    className?: string;
}

const getGridClass = (variant: "simple" | "with-center") => {
    return variant === "with-center"
        ? "grid-cols-[1fr_1fr_minmax(100px,1.5fr)_1fr_1fr]"
        : "grid-cols-2";
};

const getColSpan = (variant: "simple" | "with-center") => {
    return variant === "with-center" ? "col-span-2" : "";
};

/**
 * TeamComparisonHeader - Reusable header component for team comparison sections
 * Displays home and away team with logos, names, and optional subtitles
 */
export const TeamComparisonHeader: React.FC<TeamComparisonHeaderProps> = ({
    homeTeamName,
    awayTeamName,
    homeTeamLogo,
    awayTeamLogo,
    homeTeamImage,
    awayTeamImage,
    homeSubtitle,
    awaySubtitle,
    variant = "simple",
    textOverflow = "truncate",
    className,
}) => {
    const gridClass = getGridClass(variant);
    const colSpan = getColSpan(variant);
    const textClass = textOverflow === "break-words" ? "break-words" : "truncate";

    const baseTypography = {
        name: `text-sm font-semibold text-foreground ${textClass}`,
        ...(homeSubtitle || awaySubtitle
            ? { subtitle: "text-xs font-medium text-muted-foreground" }
            : {}),
    };

    return (
        <div className={cn("grid bg-muted border-b border-border", gridClass, className)}>
            {/* Home team */}
            <div className={cn("px-3 py-3 min-w-0", colSpan)}>
                <EntityDisplay
                    name={homeTeamName}
                    subtitle={homeSubtitle}
                    image={homeTeamImage}
                    imageUrl={homeTeamLogo}
                    styles={{
                        imageSize: "md",
                        typography: baseTypography,
                    }}
                />
            </div>

            {/* Center spacer (only for with-center variant) */}
            {variant === "with-center" && <div className="px-3 py-3" />}

            {/* Away team */}
            <div className={cn("px-3 py-3 min-w-0 flex justify-end", colSpan)}>
                <EntityDisplay
                    name={awayTeamName}
                    subtitle={awaySubtitle}
                    image={awayTeamImage}
                    imageUrl={awayTeamLogo}
                    imagePosition="right"
                    styles={{
                        imageSize: "md",
                        textAlign: "right",
                        typography: baseTypography,
                    }}
                />
            </div>
        </div>
    );
};

